import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FILE_CATEGORIES,
  formatBytes,
  formatCents,
  grossCents,
  PLAN_LABELS,
  PLANS,
  STORAGE_CATEGORY_LABELS,
  STORAGE_CRITICAL_PERCENT,
  STORAGE_PACKS,
  STORAGE_PLAN_BYTES,
  STORAGE_WARN_PERCENT,
  type DeleteStorageFilesResult,
  type FileCategory,
  type Paginated,
  type StorageFile,
  type StoragePackKey,
  type StorageUsage,
} from '@kontor/shared';
import { api, ApiError, queryString } from '../api';
import { InfoHint } from '../components/InfoHint';
import { PlanIcon } from '../components/PlanIcon';
import { SortTh } from '../components/SortTh';
import '../modules/documents/documents.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
const timeFormat = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const key = (f: { category: FileCategory; id: string }) => `${f.category}:${f.id}`;
/** So viele Dateien zeigt eine Seite der Dateiliste, höchstens */
const PAGE_SIZE = 25;

/** Seitenzahlen für die Blätterleiste: erste, letzte und die Nachbarn der aktuellen, dazwischen Lücken (null) */
function pageNumbers(page: number, last: number): (number | null)[] {
  const wanted = [...new Set([1, page - 1, page, page + 1, last])].filter((n) => n >= 1 && n <= last).sort((a, b) => a - b);
  const out: (number | null)[] = [];
  for (const n of wanted) {
    if (out.length > 0 && n - (out[out.length - 1] as number) > 1) out.push(null);
    out.push(n);
  }
  return out;
}

/**
 * Einrichtung → Speicherverwaltung (KON-164): Wie viel Speicher ist belegt (Dateien aller Module und Datenbank), wie viel gibt es im Paket, Zusatzspeicher buchen und
 * kündigen, und alle Dateien ansehen und einzeln oder gesammelt löschen. Das Belegarchiv der Buchhaltung bleibt wegen der Aufbewahrungsfrist.
 */
export function StoragePage() {
  const client = useQueryClient();
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const usage = useQuery({ queryKey: ['storage', 'usage'], queryFn: async () => (await api<{ data: StorageUsage }>('GET', '/storage/usage')).data });
  const store = (u: StorageUsage) => client.setQueryData(['storage', 'usage'], u);

  const refresh = useMutation({
    mutationFn: async () => (await api<{ data: StorageUsage }>('GET', '/storage/usage?refresh=1')).data,
    onSuccess: store,
  });
  const book = useMutation({
    mutationFn: async (pack: StoragePackKey) => (await api<{ data: StorageUsage }>('POST', '/storage/addons', { pack })).data,
    onSuccess: (u) => {
      store(u);
      setNotice({ ok: true, text: 'Der Zusatzspeicher ist gebucht und steht sofort zur Verfügung.' });
    },
    onError: (e) => setNotice({ ok: false, text: errorText(e, 'Die Buchung hat nicht geklappt.') }),
  });
  const cancel = useMutation({
    mutationFn: async (id: string) => (await api<{ data: StorageUsage }>('POST', `/storage/addons/${id}/cancel`)).data,
    onSuccess: (u) => {
      store(u);
      setNotice({ ok: true, text: 'Das Paket ist gekündigt.' });
    },
    onError: (e) => setNotice({ ok: false, text: errorText(e, 'Die Kündigung hat nicht geklappt.') }),
  });

  if (usage.isLoading) return <p className="muted">Wird geladen …</p>;
  if (usage.isError || !usage.data) return <div className="alert alert--error">{(usage.error as Error)?.message ?? 'Der Speicherverbrauch konnte nicht geladen werden.'}</div>;
  const u = usage.data;
  const free = Math.max(0, u.quotaBytes - u.usedBytes);
  const level = u.percent >= 100 ? 'full' : u.percent >= STORAGE_CRITICAL_PERCENT ? 'critical' : u.percent >= STORAGE_WARN_PERCENT ? 'warn' : 'ok';
  const active = u.addons.filter((a) => a.status === 'active');
  const monthly = active.reduce((s, a) => s + a.priceCents, 0);

  return (
    <div className="storage">
      {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--error'}`} role={notice.ok ? 'status' : 'alert'}>{notice.text}</div>}
      {level === 'full' && <div className="alert alert--error" role="alert">Ihr Speicher ist voll. Neue Dateien lassen sich nicht mehr hochladen. Löschen Sie Dateien oder buchen Sie unten mehr Speicher.</div>}
      {level === 'critical' && <div className="alert alert--error" role="alert">Ihr Speicher ist fast voll (noch {formatBytes(free)}). Löschen Sie Dateien oder buchen Sie mehr Speicher.</div>}
      {level === 'warn' && <div className="alert" role="status">Ihr Speicher ist zu {Math.round(u.percent)} % belegt. Planen Sie rechtzeitig mehr Platz ein.</div>}

      <section className="card" aria-labelledby="storage-usage">
        <h2 id="storage-usage">Speicherverbrauch</h2>
        <div className="storage-big">
          <strong>{formatBytes(u.usedBytes)}</strong>
          <span>von {formatBytes(u.quotaBytes)} belegt ({u.percent.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %)</span>
          <span className="muted">noch {formatBytes(free)} frei</span>
        </div>
        <div className="storage-meter" role="img" aria-label={`${u.percent} Prozent von ${formatBytes(u.quotaBytes)} belegt`}>
          {u.categories.filter((c) => c.bytes > 0).map((c) => (
            <span key={c.category} className={`storage-meter__part storage-cat--${c.category}`} style={{ width: `${Math.max(0.3, Math.min(100, (c.bytes / Math.max(u.quotaBytes, u.usedBytes)) * 100))}%` }} title={`${c.label}: ${formatBytes(c.bytes)}`} />
          ))}
        </div>
        <ul className="storage-legend">
          {u.categories.map((c) => (
            <li key={c.category}>
              <span className={`storage-legend__dot storage-cat--${c.category}`} aria-hidden="true" />
              <span>{c.label}{c.category !== 'database' && c.count > 0 ? ` (${c.count})` : ''}</span>
              <span className="storage-legend__value">{formatBytes(c.bytes)}</span>
            </li>
          ))}
        </ul>
        <p className="muted small">
          Gezählt werden alle Dateien Ihrer Module und die Datenbank. Die Größe der Datenbank ist eine Schätzung aus den Daten Ihres Unternehmens (mit Zuschlag für Indizes), Stand {timeFormat.format(new Date(u.databaseComputedAt))} Uhr.{' '}
          <button type="button" className="link-button" disabled={refresh.isPending} onClick={() => refresh.mutate()}>{refresh.isPending ? 'Wird berechnet …' : 'Neu berechnen'}</button>
          <InfoHint label="Datenbank">Die Datenbank enthält Kontakte, Anfragen, Rechnungen, Aufträge und alle weiteren Daten. Sie wächst langsam. Die Schätzung summiert die Größe aller Zeilen Ihres Unternehmens und rechnet 30 % für Indizes dazu.</InfoHint>
        </p>
      </section>

      <section className="card" aria-labelledby="storage-plan">
        <h2 id="storage-plan">Ihr Speicher im Paket</h2>
        <div className="storage-plans">
          {PLANS.map((p) => (
            <article key={p} className={`storage-plan${p === u.plan ? ' is-current' : ''}`} aria-label={`Paket ${PLAN_LABELS[p]}`}>
              <header><PlanIcon plan={p} size={22} title /><strong>{PLAN_LABELS[p]}</strong>{p === u.plan && <span className="chart-badge chart-badge--own">Ihr Paket</span>}</header>
              <p className="storage-pack__price">{formatBytes(STORAGE_PLAN_BYTES[p])} inklusive</p>
            </article>
          ))}
        </div>
        <p className="muted small">Ihr Kontingent: {formatBytes(u.planBytes)} im Paket{u.addonBytes > 0 ? ` plus ${formatBytes(u.addonBytes)} Zusatzspeicher` : ''} = {formatBytes(u.quotaBytes)}.</p>
      </section>

      <section className="card" aria-labelledby="storage-book">
        <h2 id="storage-book">Zusatzspeicher buchen</h2>
        <p className="muted small">Mehr Speicher gibt es in Paketen. Das Paket gilt sofort und lässt sich jederzeit kündigen, solange Ihre Daten danach noch hineinpassen. Preise je Monat, netto zuzüglich Umsatzsteuer. Abgerechnet wird mit Ihrer nächsten Rechnung für Wirkia.</p>
        <div className="storage-packs">
          {STORAGE_PACKS.map((p) => (
            <div key={p.key} className="storage-pack">
              <strong>+ {p.label}</strong>
              <span className="storage-pack__price">{formatCents(p.priceCents)} pro Monat</span>
              <span className="muted small">{formatCents(Math.round(p.priceCents / (p.bytes / 1024 ** 3)))} je GB</span>
              <button type="button" className="btn btn--primary btn--sm" disabled={book.isPending} onClick={() => window.confirm(`${p.label} Zusatzspeicher für ${formatCents(p.priceCents)} pro Monat (netto) jetzt buchen?`) && book.mutate(p.key)}>
                {book.isPending && book.variables === p.key ? 'Wird gebucht …' : 'Buchen'}
              </button>
            </div>
          ))}
        </div>
        {u.addons.length > 0 && (
          <>
            <h3>Gebuchte Pakete{monthly > 0 ? ` (${formatCents(monthly)} pro Monat netto)` : ''}</h3>
            <ul className="storage-addons">
              {u.addons.map((a) => (
                <li key={a.id} className={a.status === 'cancelled' ? 'is-cancelled' : undefined}>
                  <strong>+ {a.label}</strong>
                  <span>{formatCents(a.priceCents)} pro Monat</span>
                  <span className="muted small">gebucht am {dateFormat.format(new Date(a.bookedAt))}{a.bookedByName ? ` von ${a.bookedByName}` : ''}{a.cancelledAt ? `, gekündigt am ${dateFormat.format(new Date(a.cancelledAt))}` : ''}</span>
                  {a.status === 'active' && (
                    <button type="button" className="btn btn--secondary btn--sm" disabled={cancel.isPending} onClick={() => window.confirm(`Paket + ${a.label} kündigen? Das geht nur, wenn Ihre Daten danach noch in den Speicher passen.`) && cancel.mutate(a.id)}>
                      Kündigen
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <FileManager onChanged={() => { void client.invalidateQueries({ queryKey: ['storage', 'usage'] }); void client.invalidateQueries({ queryKey: ['documents'] }); }} />
    </div>
  );
}

/** Alle Dateien aller Module in einer Liste, mit Suche, Filter, Sortierung und Löschen einzeln oder mehrere auf einmal */
function FileManager({ onChanged }: { onChanged: () => void }) {
  const client = useQueryClient();
  const [category, setCategory] = useState<'' | FileCategory>('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('-size');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<string, StorageFile>>(new Map());
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const files = useQuery({
    queryKey: ['storage', 'files', { category, q, sort, page }],
    queryFn: () => api<Paginated<StorageFile>>('GET', `/storage/files${queryString({ category, q, sort, page, perPage: PAGE_SIZE })}`),
    placeholderData: keepPreviousData,
  });
  const remove = useMutation({
    mutationFn: async (items: StorageFile[]) => (await api<{ data: DeleteStorageFilesResult }>('POST', '/storage/files/delete', { items: items.map((f) => ({ category: f.category, id: f.id })) })).data,
    onSuccess: (r) => {
      setSelected(new Map());
      client.invalidateQueries({ queryKey: ['storage', 'files'] });
      onChanged();
      setResult({
        ok: r.skipped.length === 0,
        text: `${r.deleted} ${r.deleted === 1 ? 'Datei' : 'Dateien'} gelöscht, ${formatBytes(r.freedBytes)} frei.${r.skipped.length > 0 ? ` ${r.skipped.length} nicht gelöscht: ${[...new Set(r.skipped.map((s) => s.reason))].join(' ')}` : ''}`,
      });
    },
    onError: (e) => setResult({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });

  const rows = files.data?.data ?? [];
  const deletableRows = rows.filter((f) => f.deletable);
  const allOnPage = deletableRows.length > 0 && deletableRows.every((f) => selected.has(key(f)));
  const selectedList = [...selected.values()];
  const selectedBytes = selectedList.reduce((s, f) => s + f.sizeBytes, 0);
  const toggle = (f: StorageFile) =>
    setSelected((m) => {
      const next = new Map(m);
      if (next.has(key(f))) next.delete(key(f));
      else next.set(key(f), f);
      return next;
    });
  const confirmDelete = (items: StorageFile[]) =>
    window.confirm(
      items.length === 1
        ? `„${items[0]!.name}“ (${formatBytes(items[0]!.sizeBytes)}) endgültig löschen? Das lässt sich nicht rückgängig machen.`
        : `${items.length} Dateien (${formatBytes(items.reduce((s, f) => s + f.sizeBytes, 0))}) endgültig löschen? Das lässt sich nicht rückgängig machen.`,
    ) && remove.mutate(items);

  return (
    <section className="card storage-files" aria-labelledby="storage-files">
      <h2 id="storage-files">Alle Dateien verwalten</h2>
      <p className="muted small">Hier stehen die Dateien aller Module in einer Liste. Löschen ist endgültig: Die Datei verschwindet aus dem Modul, zu dem sie gehört. Das Belegarchiv der Buchhaltung bleibt wegen der gesetzlichen Aufbewahrungsfrist.</p>
      {result && <div className={`alert ${result.ok ? 'alert--ok' : 'alert--error'} storage-result`} role="status">{result.text}</div>}
      <div className="storage-files__bar">
        <input className="input" type="search" value={q} placeholder="Dateiname oder Bezug suchen" aria-label="Dateien suchen" onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <select className="input" value={category} aria-label="Bereich" onChange={(e) => { setCategory(e.target.value as '' | FileCategory); setPage(1); }}>
          <option value="">Alle Bereiche</option>
          {FILE_CATEGORIES.map((c) => <option key={c} value={c}>{STORAGE_CATEGORY_LABELS[c]}</option>)}
        </select>
        <button type="button" className="btn btn--danger btn--sm" disabled={selectedList.length === 0 || remove.isPending} onClick={() => confirmDelete(selectedList)}>
          {selectedList.length === 0 ? 'Ausgewählte löschen' : `${selectedList.length} ausgewählte löschen (${formatBytes(selectedBytes)})`}
        </button>
        {selectedList.length > 0 && <button type="button" className="btn btn--secondary btn--sm" onClick={() => setSelected(new Map())}>Auswahl aufheben</button>}
      </div>
      {files.isError && <div className="alert alert--error">{(files.error as Error).message}</div>}
      <div className="table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <th className="storage-check">
                <input type="checkbox" aria-label="Alle Dateien dieser Seite auswählen" checked={allOnPage} disabled={deletableRows.length === 0} onChange={() => setSelected((m) => { const next = new Map(m); for (const f of deletableRows) (allOnPage ? next.delete(key(f)) : next.set(key(f), f)); return next; })} />
              </th>
              <SortTh label="Datei" sortKey="name" sort={sort} onSort={setSort} />
              <SortTh label="Bereich" sortKey="category" sort={sort} onSort={setSort} />
              <th>Bezug</th>
              <SortTh label="Größe" sortKey="size" sort={sort} onSort={setSort} first="desc" className="num" />
              <SortTh label="Hochgeladen" sortKey="createdAt" sort={sort} onSort={setSort} first="desc" />
              <th />
            </tr>
          </thead>
          <tbody>
            {files.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {files.data && rows.length === 0 && <tr><td colSpan={7} className="empty">{q || category ? 'Keine Dateien zu dieser Suche.' : 'Noch keine Dateien.'}</td></tr>}
            {rows.map((f) => (
              <tr key={key(f)} className={selected.has(key(f)) ? 'is-selected' : undefined}>
                <td className="storage-check">
                  {f.deletable ? <input type="checkbox" aria-label={`${f.name} auswählen`} checked={selected.has(key(f))} onChange={() => toggle(f)} /> : <span className="storage-lock" title={f.lockedReason ?? ''} aria-label={f.lockedReason ?? 'gesperrt'}>🔒</span>}
                </td>
                <td data-label="Datei"><strong>{f.name}</strong>{f.uploadedByName && <div className="muted small">von {f.uploadedByName}</div>}</td>
                <td data-label="Bereich">{STORAGE_CATEGORY_LABELS[f.category]}</td>
                <td data-label="Bezug">{f.context ?? '–'}</td>
                <td data-label="Größe" className="num">{formatBytes(f.sizeBytes)}</td>
                <td data-label="Hochgeladen">{dateFormat.format(new Date(f.createdAt))}</td>
                <td>
                  {f.deletable ? (
                    <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} aria-label={`${f.name} löschen`} onClick={() => confirmDelete([f])}>Löschen</button>
                  ) : (
                    <span className="muted small">Aufbewahrungspflicht</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {files.data && (
        <nav className="pager pager--numbers" aria-label="Seiten der Dateiliste">
          <span className="muted small">
            {files.data.meta.total === 0 ? 'Keine Dateien' : `${(page - 1) * PAGE_SIZE + 1} bis ${Math.min(page * PAGE_SIZE, files.data.meta.total)} von ${files.data.meta.total} Dateien`}
          </span>
          {files.data.meta.lastPage > 1 && (
            <span className="pager__buttons">
              <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Zurück</button>
              {pageNumbers(page, files.data.meta.lastPage).map((n, i) =>
                n === null ? (
                  <span key={`gap-${i}`} className="muted" aria-hidden="true">…</span>
                ) : (
                  <button key={n} type="button" className={`btn btn--sm ${n === page ? 'btn--primary' : 'btn--secondary'}`} aria-current={n === page ? 'page' : undefined} aria-label={`Seite ${n}`} onClick={() => setPage(n)}>{n}</button>
                ),
              )}
              <button type="button" className="btn btn--secondary btn--sm" disabled={page >= files.data.meta.lastPage} onClick={() => setPage(page + 1)}>Weiter</button>
            </span>
          )}
        </nav>
      )}
    </section>
  );
}
